import type { RobotScheduleMapGroup } from '../types/RobotScheduleMap.types';
import { compareMapGroupsAscending, MAP_GROUP_NAME_OTHERS } from './map';
import { compareNamesAscending } from './sort';
import type { AvailableMap } from 'app/modules/machine-inventory/ScheduleClient';
import type { MapGroup } from 'app/cross-cutting-concerns/communication/interfaces/pra-api-graphql';

export const filterMapAssignmentGroups = (
  groups: RobotScheduleMapGroup[],
  searchTerm: string
): RobotScheduleMapGroup[] => {
  const normalizedSearchTerm = searchTerm.trim().toLowerCase();

  if (!normalizedSearchTerm) {
    return groups;
  }

  return groups.reduce<RobotScheduleMapGroup[]>((filteredGroups, group) => {
    const groupNameMatches =
      group.groupId !== MAP_GROUP_NAME_OTHERS && group.groupName.toLowerCase().includes(normalizedSearchTerm);

    if (groupNameMatches) {
      filteredGroups.push(group);
      return filteredGroups;
    }

    const matchingMaps = group.maps.filter(map => map.name.toLowerCase().includes(normalizedSearchTerm));

    if (matchingMaps.length > 0) {
      filteredGroups.push({ ...group, maps: matchingMaps });
    }

    return filteredGroups;
  }, []);
};

export const buildMapAssignmentGroups = (
  mapGroups: MapGroup[],
  maps: AvailableMap[],
  locale?: string
): RobotScheduleMapGroup[] => {
  const groups = mapGroups.map(mapGroup => ({
    groupId: mapGroup.id,
    groupName: mapGroup.name,
    maps: maps.filter(map => map.groupId === mapGroup.id).sort((a, b) => compareNamesAscending(a.name, b.name, locale)),
  }));

  const others = maps.filter(map => !map.groupId).sort((a, b) => compareNamesAscending(a.name, b.name, locale));

  if (others.length > 0) {
    groups.push({
      groupId: MAP_GROUP_NAME_OTHERS,
      groupName: MAP_GROUP_NAME_OTHERS,
      maps: others,
    });
  }

  return groups.sort((a, b) => compareMapGroupsAscending(a, b, locale));
};

export const getMapsCheckedState = (
  maps: AvailableMap[],
  selectedMapIds: Set<string>
): { checked: boolean; indeterminate: boolean } => {
  const selectedCount = maps.filter(map => selectedMapIds.has(map.id)).length;

  return {
    checked: maps.length > 0 && selectedCount === maps.length,
    indeterminate: selectedCount > 0 && selectedCount < maps.length,
  };
};

export const getGroupCheckedState = (
  group: RobotScheduleMapGroup,
  selectedMapIds: Set<string>
): { checked: boolean; indeterminate: boolean } => getMapsCheckedState(group.maps, selectedMapIds);
